import React, { useEffect, useMemo, useState } from 'react'

interface PagingProps {
  onPageChange?: (page: { selected: number }) => void
  total: number
  perPage: number
  initialPage?: number
}

const PAGE_WINDOW = 2

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const BreakItem = () => (
  <li className="page-item disabled">
    <span className="page-link">...</span>
  </li>
)

export default function Paging({
  onPageChange,
  total,
  perPage,
  initialPage = 0,
}: PagingProps) {
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / perPage)),
    [total, perPage]
  )

  const [current, setCurrent] = useState(
    clamp(Number(initialPage) === 0 ? 0 : Number(initialPage) - 1, 0, pageCount - 1)
  )

  useEffect(() => {
    setCurrent(
      clamp(Number(initialPage) === 0 ? 0 : Number(initialPage) - 1, 0, pageCount - 1)
    )
  }, [initialPage, pageCount])

  const selectPage = (index: number) => {
    const next = clamp(index, 0, pageCount - 1)
    setCurrent(next)
    onPageChange && onPageChange({ selected: next })
  }

  const buildPages = () => {
    if (pageCount <= PAGE_WINDOW * 2 + 5) {
      return Array.from({ length: pageCount }, (_, index) => index)
    }

    const start = Math.max(1, current - PAGE_WINDOW)
    const end = Math.min(pageCount - 2, current + PAGE_WINDOW)
    const items: Array<number | 'break'> = [0]

    if (start > 1) {
      items.push('break')
    }

    for (let page = start; page <= end; page += 1) {
      items.push(page)
    }

    if (end < pageCount - 2) {
      items.push('break')
    }

    items.push(pageCount - 1)
    return items
  }

  const pages = buildPages()

  return (
    <ul className="pagination justify-content-center">
      <li className={`page-item ${current === 0 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => selectPage(current - 1)}
          disabled={current === 0}
        >
          Previous page
        </button>
      </li>
      {pages.map((item, index) => (
        item === 'break' ? (
          <BreakItem key={`break-${index}`} />
        ) : (
          <li
            key={item}
            className={`page-item ${current === item ? 'active' : ''}`}
          >
            <button
              className="page-link"
              onClick={() => selectPage(item)}
              aria-current={current === item ? 'page' : undefined}
            >
              {item + 1}
            </button>
          </li>
        )
      ))}
      <li className={`page-item ${current === pageCount - 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => selectPage(current + 1)}
          disabled={current === pageCount - 1}
        >
          Next page
        </button>
      </li>
    </ul>
  )
}
